// Customers (prototype 50-customers.js customersPage()) and the import wizard (MODALS.import):
// upload → match columns → check (import_customers dry run) → import. Spec 03 R-23, R-24, AT-18, AT-19.
import { Money } from '@xr/ui';
import { useCallback, useEffect, useState } from 'react';
import { plain } from '../lib/errors';
import { L, N } from '../lib/prefs';
import { can, refreshSetup, useApp } from '../lib/session';
import { rpc, sb } from '../lib/supabase';
import { FileError, type Sheet, parseCSV, readTableFile } from '../lib/table-file';
import { soon } from '../shell/soon';
import { Btn, Card, Empty, Ic, PageHead, Select } from '../ui/kit';
import { closeModal, openModal, retitle } from '../ui/modal';
import { Pill, SearchBox, Table } from '../ui/table';
import { toast } from '../ui/toast';
import { Wiz } from '../ui/wizard';
import { SAMPLE_CUSTOMERS_CSV } from './samples';

type Customer = { id: string; name_en: string; name_ar: string | null; trn: string | null; contact: string | null; email: string | null };
type Row = { row: number; result: 'new' | 'duplicate' | 'invalid'; errors?: string[] };
type Result = { rows_read: number; added: number; skipped_duplicate: number; skipped_invalid: number; rows: Row[] };

// prototype IMP_FIELDS: [key, English, Arabic, how a column name is recognised]
const FIELDS: [string, string, string, RegExp][] = [
  ['name', 'Name (English)', 'الاسم (إنجليزي)', /name|customer|company|الاسم|العميل/i], ['name_ar', 'Name (Arabic)', 'الاسم (عربي)', /arabic|عربي/i],
  ['trn', 'TRN', 'الرقم الضريبي', /trn|tax|vat|ضريبي/i], ['contact', 'Contact person', 'جهة الاتصال', /contact|person/i],
  ['phone', 'Phone', 'الهاتف', /phone|mobile|tel|هاتف/i], ['email', 'Email', 'البريد', /mail|بريد/i],
];
export function autoMap(head: string[], fields: [string, string, string, RegExp][]) {
  const map: Record<string, number> = {};
  for (const [k, , , re] of fields) map[k] = head.findIndex((h, j) => re.test(h) && !Object.values(map).includes(j));
  return map;
}
export const fileErr = (e: unknown) => {
  const c = e instanceof FileError ? e.code : 'unreadable';
  return c === 'type' ? L('This file type is not accepted. Use CSV or Excel (.xlsx).', 'نوع الملف غير مقبول. استخدم CSV أو إكسل (.xlsx).')
    : c === 'size' ? L('The file is larger than 5 MB. Split it and import each part.', 'الملف أكبر من 5 ميغابايت. قسّمه واستورد كل جزء.')
    : c === 'empty' ? L('The file has no rows to import. The first row holds the column names.', 'لا توجد صفوف للاستيراد. الصف الأول لأسماء الأعمدة.')
    : L('This file could not be read. Save it again as CSV and try again.', 'تعذرت قراءة الملف. احفظه مجدداً بصيغة CSV وحاول مرة أخرى.');
};
const ERR: Record<string, [string, string]> = { name_required: ['No name', 'بدون اسم'], trn_invalid: ['TRN must be 15 digits', 'الرقم الضريبي 15 رقماً'], email_invalid: ['Bad email', 'بريد غير صالح'], too_long: ['Too long', 'طويل جداً'] };

/** The drop zone of step 1, shared by both import wizards. */
export function DropFile({ onSheet, hint }: { onSheet: (name: string, s: Sheet) => void; hint: string }) {
  const [over, setOver] = useState(false);
  const take = async (f: File | undefined) => {
    if (!f) return;
    try { onSheet(f.name, await readTableFile(f)); } catch (e) { toast(fileErr(e), { tone: 'bad' }); }
  };
  return (
    <label className={'drop' + (over ? ' over' : '')} onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); void take(e.dataTransfer.files[0]); }}>
      <Ic n="upload" />
      <b>{L('Drop an Excel or CSV file here, or click to choose', 'أفلت ملف إكسل أو CSV هنا أو اضغط للاختيار')}</b>
      <span>{hint}</span>
      <input type="file" id="imp-file" accept=".csv,text/csv,.xlsx" className="vh" onChange={e => { void take(e.target.files?.[0]); e.target.value = ''; }} />
    </label>
  );
}

function ImportWizard({ existing, onDone }: { existing: Customer[]; onDone: () => void }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<{ name: string; sheet: Sheet } | null>(null);
  const [map, setMap] = useState<Record<string, number>>({});
  const [res, setRes] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const steps = [L('Upload', 'رفع'), L('Match columns', 'مطابقة الأعمدة'), L('Check', 'مراجعة')];
  const load = (name: string, sheet: Sheet) => { const m = autoMap(sheet.head, FIELDS); if (m.name_ar === m.name) m.name_ar = -1; setFile({ name, sheet }); setMap(m); setStep(2); };
  const payload = () => file!.sheet.rows.map(r => Object.fromEntries(FIELDS.map(([k]) => [k, (map[k] ?? -1) >= 0 ? (r[map[k]!] || '').trim() : ''])));
  const check = async () => {
    setBusy(true);
    try { setRes(await rpc<Result>('import_customers', { p_file_name: file!.name, p_rows: payload(), p_dry_run: true })); setStep(3); }
    catch (e) { toast(plain(e), { tone: 'bad' }); }
    finally { setBusy(false); }
  };
  const go = async () => {
    setBusy(true);
    try {
      const r = await rpc<Result>('import_customers', { p_file_name: file!.name, p_rows: payload(), p_dry_run: false });
      closeModal(); onDone();
      toast(L(`${r.added} customers imported`, `تم استيراد ${r.added} عملاء`));
    } catch (e) { toast(plain(e), { tone: 'bad' }); }
    finally { setBusy(false); }
  };
  const head = <Wiz steps={steps} cur={step} />;
  useEffect(() => {
    if (step === 1) retitle(L('Import customers', 'استيراد العملاء'));
    else if (step === 2 && file) retitle(L('Match columns', 'مطابقة الأعمدة'), file.name + ' · ' + L(file.sheet.rows.length + ' rows', file.sheet.rows.length + ' صفوف'));
    else if (step === 3) retitle(L('Check before importing', 'راجع قبل الاستيراد'));
  }, [step, file]);
  if (step === 1) return (
    <>
      <div className="m-body">{head}
        <DropFile onSheet={load} hint={L('One customer per row. The first row has the column names.', 'عميل في كل صف. الصف الأول لأسماء الأعمدة.')} />
        <div className="or"><span>{L('or', 'أو')}</span></div>
        <Btn kind="ghost" cls="wide" icon="doc" onClick={() => load('customers-sample.csv', parseCSV(SAMPLE_CUSTOMERS_CSV))}>{L('Use a sample file', 'استخدم ملفاً تجريبياً')}</Btn>
        <p className="muted sm">{L('Tip: export the customer list from your accounting software and upload it as it is. You match the columns next.', 'نصيحة: صدّر قائمة العملاء من نظامك المحاسبي وارفعها كما هي.')}</p>
      </div>
      <footer className="m-foot"><Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn></footer>
    </>
  );
  const sh = file!.sheet;
  if (step === 2) return (
    <>
      <div className="m-body">{head}
        <div className="map">{FIELDS.map(([k, en, ar]) => (
          <div className="map-row" key={k}>
            <b>{L(en, ar)}{k === 'name' ? <> <b className="req">*</b></> : null}</b>
            <Ic n={document.documentElement.dir === 'rtl' ? 'chevL' : 'chevR'} />
            <Select<number> id={'map-' + k} value={map[k] ?? -1} onChange={v => setMap({ ...map, [k]: v })} label={L(en, ar)} opts={[[-1, L('Don\'t import', 'لا تستورد')], ...sh.head.map((h, i) => [i, h] as [number, string])]} />
            <small className="muted ell">{(map[k] ?? -1) >= 0 ? sh.rows.slice(0, 2).map(r => r[map[k]!]).join(', ') : ''}</small>
          </div>
        ))}</div>
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={() => setStep(1)}>{L('Back', 'رجوع')}</Btn>
        <Btn kind="primary" busy={busy} disabled={!((map.name ?? -1) >= 0)} why={L('Match the name column', 'طابق عمود الاسم')} onClick={() => void check()}>{L('Next', 'التالي')}</Btn>
      </footer>
    </>
  );
  const rows = payload();
  const r = res!;
  const matchOf = (i: number) => {
    const o = rows[i]!; const nm = String(o.name).toLowerCase(); const trn = String(o.trn).replace(/\s/g, '');
    const c = existing.find(x => (trn && x.trn === trn) || x.name_en.toLowerCase() === nm);
    if (c) return N(c.name_en, c.name_ar);
    const earlier = rows.slice(0, i).find(x => String(x.name).toLowerCase() === nm || (trn && String(x.trn).replace(/\s/g, '') === trn));
    return earlier ? String(earlier.name) : '';
  };
  return (
    <>
      <div className="m-body">{head}
        <div className="mini-kpis three">
          <div className="good"><span>{L('New customers', 'عملاء جدد')}</span><b>{r.added}</b></div>
          <div className="warn"><span>{L('Already in your list', 'موجودون بالفعل')}</span><b>{r.skipped_duplicate}</b></div>
          <div className="bad"><span>{L('Need fixing', 'تحتاج تصحيحاً')}</span><b>{r.skipped_invalid}</b></div>
        </div>
        <div className="t-wrap stackable"><table className="tbl stack">
          <thead><tr><th>{L('Name', 'الاسم')}</th><th>TRN</th><th>{L('Email', 'البريد')}</th><th>{L('Result', 'النتيجة')}</th></tr></thead>
          <tbody>{r.rows.map(x => { const o = rows[x.row - 1]!; return (
            <tr key={x.row}>
              <td className="ga" style={{ gridColumn: '1/-1' }}>{String(o.name) || '—'}{o.name_ar ? <small className="sub" dir="rtl">{String(o.name_ar)}</small> : null}</td>
              <td className="mono gc">{String(o.trn).replace(/\s/g, '') || '—'}</td>
              <td className="gd ell">{String(o.email) || '—'}</td>
              <td className="ge">{x.result === 'invalid' ? <Pill tone="bad">{(x.errors || []).map(e => ERR[e] ? L(ERR[e]![0], ERR[e]![1]) : e).join(', ')}</Pill>
                : x.result === 'duplicate' ? <Pill tone="warn">{L('Skip: matches ', 'تخطي: يطابق ') + matchOf(x.row - 1)}</Pill>
                : <Pill tone="good">{L('Will be added', 'سيضاف')}</Pill>}</td>
            </tr>); })}</tbody>
        </table></div>
        {r.skipped_invalid ? <p className="muted sm">{L('Rows that need fixing are skipped. Fix them in the file and import again; nothing is added twice.', 'تُتخطى الصفوف الخاطئة. صححها واستورد مرة أخرى؛ لن يضاف شيء مرتين.')}</p> : null}
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={() => setStep(2)}>{L('Back', 'رجوع')}</Btn>
        <Btn kind="primary" busy={busy} disabled={!r.added} onClick={() => void go()}>{L(`Import ${r.added} customers`, `استيراد ${r.added} عملاء`)}</Btn>
      </footer>
    </>
  );
}

export function CustomersPage() {
  const { co } = useApp();
  const [list, setList] = useState<Customer[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');
  const id = co?.id;
  const reload = useCallback(async () => {
    if (!id) return;
    const r = await sb.from('customers').select('id,name_en,name_ar,trn,contact,email').eq('tenant_id', id).order('name_en');
    if (r.error) setFailed(true); else setList(r.data as Customer[]);
  }, [id]);
  useEffect(() => { void reload(); }, [reload]);
  const edit = can('customers');
  const importIt = () => openModal({
    title: L('Import customers', 'استيراد العملاء'), size: 'lg', bare: true,
    body: <ImportWizard existing={list || []} onDone={() => { void reload(); void refreshSetup(); }} />,
  });
  const head = <PageHead title={L('Customers', 'العملاء')} sub={L('Who pays you. Collectors pick from this list on their phones and can add new ones as they go.', 'من يدفع لك. يختار المحصلون من هذه القائمة ويمكنهم إضافة عملاء جدد.')}
    tools={list && edit ? <><Btn kind="ghost" icon="upload" onClick={importIt}>{L('Import', 'استيراد')}</Btn><Btn kind="primary" icon="plus" onClick={() => soon(L('Adding customers', 'إضافة العملاء'))}>{L('Add customer', 'إضافة عميل')}</Btn></> : null} />;
  if (failed) return <>{head}<div className="card"><Empty icon="alert" title={L('Something went wrong on this page', 'حدث خطأ في هذه الصفحة')} body={plain(null)} /></div></>;
  if (!list) return head;
  if (!list.length) return (
    <>{head}<Card><Empty icon="building" title={L('No customers yet', 'لا عملاء بعد')}
      body={L('Import your customer list from Excel or CSV, or add the first one by hand. Collectors can also add customers from the phone.', 'استورد قائمة عملائك من إكسل أو CSV، أو أضف أول عميل يدوياً. يمكن للمحصلين أيضاً الإضافة من الهاتف.')}
      action={edit ? <><Btn kind="primary" icon="upload" onClick={importIt}>{L('Import customers', 'استيراد العملاء')}</Btn> <Btn kind="ghost" icon="plus" onClick={() => soon(L('Adding customers', 'إضافة العملاء'))}>{L('Add customer', 'إضافة عميل')}</Btn></> : null} /></Card></>
  );
  const ql = q.toLowerCase().trim();
  const rows = list.filter(c => !ql || [c.name_en, c.name_ar, c.contact, c.email, c.trn].join(' ').toLowerCase().includes(ql));
  return (
    <>
      {head}
      <div className="toolbar"><SearchBox id="customers" value={q} onChange={setQ} ph={L('Name, contact, TRN, email…', 'الاسم أو جهة الاتصال أو الرقم الضريبي…')} /></div>
      <div className="card flush">
        <Table<Customer> rows={rows} rowKey={c => c.id} cols={[
          { k: 'n', label: L('Customer', 'العميل'), cls: 'grow', sort: c => N(c.name_en, c.name_ar), html: c => <><b className="ell">{N(c.name_en, c.name_ar)}</b><small className="sub">{c.contact || ''}{c.trn ? ' · TRN ' + c.trn : ''}</small></> },
          { k: 'amt', label: L('Received', 'المستلم'), num: true, html: () => <><span className="amt"><Money value={0} /></span><small className="sub">{L('0 receipts', '0 سندات')}</small></> },
          { k: 'pdc', label: L('Open cheques', 'شيكات مفتوحة'), num: true, cls: 'hide-sm', html: () => <span className="muted">—</span> },
          { k: 'last', label: L('Last payment', 'آخر دفعة'), cls: 'hide-md', html: () => <span className="muted">{L('Never', 'أبداً')}</span> },
        ]} />
      </div>
    </>
  );
}
